/**
 * CheckinTab — Single-day event combined check-in view.
 *
 * For each registered athlete the TM can in one pass:
 *   • Enter actual weigh-in weight (kg)
 *   • Mark "Ready" — it also confirms the registration and sets the weight
 *     class from the weight. A boxer in a bracket, or with a bout, keeps the
 *     class.
 *   • Reset a mark to pending
 * "End Check-in" marks every pending athlete absent.
 *
 * Shown only for open_ring tournaments (TM only).
 *
 * Backend calls (all pre-existing endpoints):
 *   PATCH /api/tournaments/:id/registrations/:regId  { is_confirmed, age_group, weight_class }  (updateRegistration)
 *   PATCH /api/tournaments/:id/weighins/:regId        { date, status, weight_kg }  (markWeighin)
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import GenderBadge from '../shared/GenderBadge';
import PushOffHint from '../PushOffHint';
import { UndoIcon, CheckIcon } from '../shared/Icons';
import { endWeighin, markWeighin, updateRegistration } from '../../api/client';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { inlineError } from '../../hooks/useApiCall';
import { logBackgroundError } from '../../observability';
import { errorCode } from '../../lib/httpError';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy, useBusyKeys } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { suggestWeightClass, weighinStatusOn } from '../../utils/tournament';
import type { MarkWeighinDto, Registration, UpdateRegistrationDto, WeightClass, WeighinStatus } from '../../types';
import { personName, nameMatches, textMatches, compareNames } from '../../utils/personName';
import { toKg } from '../../utils/weight';
import Notice from '../shared/Notice';
import { BADGE_XXS } from '../shared/badgeSize';

// ── Constants ─────────────────────────────────────────────────────────────────


// ── Types ─────────────────────────────────────────────────────────────────────

interface RowState {
  confirmed: boolean;
  age_group: string;
  weight_class: string;
  weight_kg: string;           // string so <input> stays controlled
  // Last server-known weight as a string. Used to detect "user edited" vs
  // "untouched" so server-side weight changes (e.g. socket reload after
  // another TM marks a weigh-in) refresh the input only when not dirty.
  lastServerWeight: string;
  // The last server values of the three registration fields, for the same
  // reason: a field the user has not edited takes the server value on a
  // refresh. Without them a row kept the values it had when it was first
  // built, and its next save sent them back over the change of another
  // manager (2026-09-25).
  lastServerConfirmed: boolean;
  lastServerAg: string;
  lastServerWc: string;
  status: WeighinStatus;
  error: string;
}

interface Props {
  tournamentId: string;
  tournamentDate: string;        // tournament.date_start — ISO string
  registrations: Registration[];
  weightClasses: WeightClass[];  // all WCs, loaded unfiltered by parent
  onRegistrationUpdated: () => void | Promise<unknown>;
  /** #490 — the lock that freezes the confirmed status, if any. The server
   *  refuses a change of the confirmation under it (403), except for an
   *  admin. Ready and Reset change it on most rows, so the tab turns both
   *  off for everyone, as the Boxers tab hides its confirm box. */
  lockedBy?: 'registration' | 'bouts' | null;
  /** Unlocks registration from the notice. Absent when the header does not
   *  offer the switch either (the bout list is locked, or a bout is scored). */
  onUnlockRegistration?: () => void;
  /** #605 — a lock change runs. The unlock button is disabled until the answer. */
  unlockBusy?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatusPill({ status, labels }: { status: WeighinStatus; labels: { ready: string; absent: string; pending: string } }) {
  if (status === 'ready')
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold whitespace-nowrap">
        {labels.ready}
      </span>
    );
  if (status === 'absent')
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-semibold whitespace-nowrap">
        {labels.absent}
      </span>
    );
  return (
    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 whitespace-nowrap">
      {labels.pending}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CheckinTab({
  tournamentId,
  tournamentDate,
  registrations,
  weightClasses,
  onRegistrationUpdated,
  lockedBy = null,
  onUnlockRegistration,
  unlockBusy = false,
}: Props) {
  const { t } = useI18n();
  // #531 — the counts take the plural form of the language.
  const { boxerCount, readyCount } = useLabels();
  const { showError } = useToast();
  const { confirm } = useConfirm();
  // #557 review — the warnings of End Check-in, as End Weigh-in shows them.
  const afterWrite = useAfterWrite();
  // The `checkin` block of en.ts and lt.ts holds every key, and the type
  // checks it, so no text here needs an English fallback (#458).
  const ci = t.checkin;
  // Normalise to YYYY-MM-DD
  const dateStr = tournamentDate?.slice(0, 10) ?? '';

  const [search, setSearch] = useState('');
  // #605, #664 — one write at a time for each row (Ready, Reset), and one End Check-in at a time.
  const rowBusy = useBusyKeys();
  const ending = useBusy();
  // #665 review — the rows with an open over-limit question. A double tap on
  // Ready asks one time. The provider answers no to the copy of the question,
  // and that no must not show the "refused" line under the open question.
  const asking = useRef(new Set<string>());

  // Per-row local edit state, keyed by registration id
  const [rows, setRows] = useState<Record<string, RowState>>({});

  // Initialise / refresh row state when registrations or date change
  useEffect(() => {
    setRows(prev => {
      const next: Record<string, RowState> = {};
      for (const reg of registrations) {
        const freshStatus = weighinStatusOn(reg, dateStr);
        const existing = prev[reg.id];
        const serverWeight = reg.current_weight_kg ? String(reg.current_weight_kg) : '';
        const serverConfirmed = reg.is_confirmed ?? false;
        const serverAg = reg.age_group ?? '';
        const serverWc = reg.weight_class ?? '';
        if (existing) {
          // Refresh server-derived fields when their server-side counterpart
          // changed (socket reload after another TM marks a weigh-in), unless
          // the user has typed a custom value — then preserve the dirty edit.
          const userEdited = existing.weight_kg !== existing.lastServerWeight;
          next[reg.id] = {
            ...existing,
            status: freshStatus,
            weight_kg: userEdited ? existing.weight_kg : serverWeight,
            lastServerWeight: serverWeight,
            confirmed: existing.confirmed !== existing.lastServerConfirmed ? existing.confirmed : serverConfirmed,
            age_group: existing.age_group !== existing.lastServerAg ? existing.age_group : serverAg,
            weight_class: existing.weight_class !== existing.lastServerWc ? existing.weight_class : serverWc,
            lastServerConfirmed: serverConfirmed,
            lastServerAg: serverAg,
            lastServerWc: serverWc,
          };
        } else {
          next[reg.id] = {
            confirmed: serverConfirmed,
            age_group: serverAg,
            weight_class: serverWc,
            weight_kg: serverWeight,
            lastServerWeight: serverWeight,
            lastServerConfirmed: serverConfirmed,
            lastServerAg: serverAg,
            lastServerWc: serverWc,
            status: freshStatus,
            error: '',
          };
        }
      }
      return next;
    });
  }, [registrations, dateStr]);

  const setRow = useCallback(
    (id: string, patch: Partial<RowState>) =>
      setRows(prev => ({ ...prev, [id]: { ...prev[id], ...patch } })),
    [],
  );

  // Weight classes for a given age group + gender
  function wcsForAgGender(ag: string, gender: string | undefined): WeightClass[] {
    return weightClasses.filter(wc => wc.age_group_code === ag && wc.gender === gender);
  }

  /** The upper limit of a weight class in kg. Undefined while the list of the
   *  classes is empty (it did not load). Null for the open class (999), and
   *  for a class that a loaded list does not hold: no limit to check, as
   *  before #536. NUMERIC arrives as a string. */
  function classLimit(ag: string, gender: string | undefined, wc: string): number | null | undefined {
    if (weightClasses.length === 0) return undefined;
    const found = wcsForAgGender(ag, gender).find(c => c.class_label === wc);
    if (!found) return null;
    const max = found.max_weight_kg == null ? NaN : Number(found.max_weight_kg);
    return Number.isFinite(max) && max < 999 ? max : null;
  }

  /** The limit test of Ready, for the question and for the write.
   *  #536, #547 — an athlete in a bracket or with a bout keeps the class (the
   *  server refuses a class change: REGISTRATION_IN_BRACKET,
   *  ATHLETE_ASSIGNED_TO_BOUT). `keepsClass` false: the class comes from the
   *  weight. `limit` undefined: the list of the classes did not load. `over`:
   *  the weight is over the limit of the class that the athlete keeps. */
  function readyCheck(reg: Registration, row: RowState) {
    const keepsClass = !!reg.in_bracket || (reg.bout_numbers?.length ?? 0) > 0;
    const limit = keepsClass ? classLimit(reg.age_group, reg.gender, reg.weight_class) : null;
    // #556 — rounded as the server rounds it.
    const kg = toKg(row.weight_kg);
    return { keepsClass, limit, kg, over: limit != null && kg != null && kg > limit };
  }

  // ── API helpers ─────────────────────────────────────────────────────────────

  async function saveRegistrationFields(id: string, row: RowState) {
    const reg = registrations.find(r => r.id === id)!;
    const patch: UpdateRegistrationDto = {};
    if (row.confirmed !== reg.is_confirmed)     patch.is_confirmed  = row.confirmed;
    if (row.age_group !== reg.age_group)         patch.age_group     = row.age_group;
    if (row.weight_class !== reg.weight_class)   patch.weight_class  = row.weight_class;
    if (Object.keys(patch).length === 0) return;
    await updateRegistration(tournamentId, id, patch);
  }

  async function saveWeighin(
    id: string,
    row: RowState,
    status: WeighinStatus,
  ) {
    const body: MarkWeighinDto = { date: dateStr, status };
    // #556 — the weight that the row stores, rounded as the server rounds it.
    const kg = toKg(row.weight_kg);
    if (kg != null) body.weight_kg = kg;
    await markWeighin(tournamentId, id, body);
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Find the correct weight class for an actual weight */
  function findWeightClass(actualKg: number, ageGroup: string, gender: string | undefined): string | null {
    // #458 — the shared rule (utils/tournament.ts), which sorts first. This
    // copy found the first class in list order, so it was right only while
    // the API returned the classes sorted by weight.
    return suggestWeightClass(actualKg, wcsForAgGender(ageGroup, gender).map(wc => wc.class_label));
  }

  /** Mark Ready: auto-updates weight class from actual weight, saves confirmed + weighin */
  async function markReady(id: string) {
    const row = rows[id];
    const reg = registrations.find(r => r.id === id);
    if (!row || !reg) return;
    // Over the limit of the class: the manager answers BEFORE the busy lock of
    // the row starts, so the lock is free while the person reads (#665).
    // The run() below uses the same test.
    const check = readyCheck(reg, row);
    if (check.over) {
      // A second tap while the question of the row is open: nothing.
      if (asking.current.has(id)) return;
      // The confirm button keeps the label "Confirm": the Manual names it (#665 review).
      const fill = (text: string) => text.replace('{limit}', String(check.limit)).replace('{weight}', String(check.kg));
      const ask = reg.in_bracket ? ci.overBracketConfirm : ci.overBoutConfirm;
      const refused = reg.in_bracket ? ci.overBracketLimit : ci.overBoutLimit;
      asking.current.add(id);
      let yes = false;
      try {
        yes = await confirm({ message: fill(ask), focusCancel: true });
      } finally {
        asking.current.delete(id);
      }
      if (!yes) {
        setRow(id, { error: fill(refused) });
        return;
      }
    }
    await rowBusy.run(id, async () => {
      setRow(id, { error: '' });
      try {
        // #556 — rounded as the server rounds it: the limit check and the class
        // that the desk picks use the weight that the row stores.
        const actualKg = check.kg;
        const updatedRow = { ...row, confirmed: true };
        if (check.keepsClass) {
          // #536 — an athlete in a bracket keeps the class: the server refuses a
          // class change (REGISTRATION_IN_BRACKET). #547 — so does an athlete
          // with a bout (ATHLETE_ASSIGNED_TO_BOUT): the bout keeps its class.
          // #547 — this desk serves an open ring and a hybrid, and there the
          // walk-over rule of a championship does not apply (operator,
          // 2026-10-03). Over the limit the manager decides: Confirm marks the
          // athlete ready in the class with the weight; Cancel writes nothing,
          // and a pending athlete becomes absent at End Check-in.
          // With no known limit the desk cannot check the rule: it marks nothing.
          if (check.limit === undefined) {
            setRow(id, { error: ci.limitUnknown });
            return;
          }
          // Over the limit: the manager answered the question before this run().
          updatedRow.age_group = reg.age_group;
          updatedRow.weight_class = reg.weight_class;
        } else {
          // #547 — the class comes from the weight, so the desk needs the list of
          // the classes. When it did not load, the old class would stay with no
          // message: the desk marks nothing.
          if (weightClasses.length === 0) {
            setRow(id, { error: ci.classUnknown });
            return;
          }
          // Auto-derive weight class from actual weight
          const newWc = actualKg != null ? findWeightClass(actualKg, reg.age_group, reg.gender) : null;
          if (newWc && newWc !== reg.weight_class) {
            updatedRow.weight_class = newWc;
          }
        }
        await saveRegistrationFields(id, updatedRow);
        await saveWeighin(id, row, 'ready');
        setRow(id, {
          confirmed: true, status: 'ready', weight_class: updatedRow.weight_class,
          lastServerConfirmed: true, lastServerAg: updatedRow.age_group, lastServerWc: updatedRow.weight_class,
        });
        // #664 — the write committed: a failed reload warns, it never shows "failed".
        await afterWrite({ source: 'CheckinTab:ready', refresh: async () => onRegistrationUpdated() });
      } catch (err) {
        setRow(id, { error: inlineError(err, ci.readyFailed) });
        // #547 — a stale row: the athlete got a bout or a bracket seat since the
        // last load. Reload, so the next click keeps the class. #664 — the write
        // did NOT commit here, so a failed reload goes to the telemetry only:
        // the "saved" warning of afterWrite() would be false.
        const code = errorCode(err);
        if (code === 'ATHLETE_ASSIGNED_TO_BOUT' || code === 'REGISTRATION_IN_BRACKET') {
          await Promise.resolve().then(onRegistrationUpdated).catch(logBackgroundError('CheckinTab:stale'));
        }
      }
    });
  }

  /** End Check-in. #547 — an open ring and a hybrid end the check-in
   *  directly, with any number of managers: no proposal, no 4-eye rule.
   *  #557 review — the answer warns about a bout whose two athletes are
   *  absent (BOTH_ATHLETES_ABSENT). Before, the tab ignored the answer, so
   *  the actor never saw it, and the manager summary leaves out the actor. */
  async function handleEndCheckin() {
    if (ending.busy) return;
    if (!(await confirm({ message: ci.endCheckinConfirm, confirmLabel: ci.endCheckinBtn, destructive: true }))) return;
    await ending.run(async () => {
      try {
        const res = await endWeighin(tournamentId, dateStr);
        await afterWrite({ source: 'CheckinTab:end', warnings: res.data.warnings, refresh: async () => onRegistrationUpdated() });
      } catch (err) {
        showError(err);
      }
    });
  }

  /** Reset to Pending (undo a mark) */
  async function resetPending(id: string) {
    const row = rows[id];
    const reg = registrations.find(r => r.id === id);
    if (!row || !reg) return;
    await rowBusy.run(id, async () => {
      setRow(id, { error: '' });
      try {
        // In an open ring, Ready is the weigh-in AND the payment (operator,
        // 2026-09-25), and it confirms the registration. Reset undoes both.
        // It reset the weigh-in only, so the athlete stayed in the confirmed
        // list of the Boxers tab. Only the confirmation is sent: an unsaved
        // edit of the class stays in the row.
        // #536 — an athlete in a bracket stays confirmed: the server refuses to
        // un-confirm it while its bracket exists (CANNOT_UNCONFIRM_BRACKET_EXISTS).
        // Reset then undoes the weigh-in only.
        const unconfirm = reg.is_confirmed && !reg.in_bracket;
        if (unconfirm) {
          await updateRegistration(tournamentId, id, { is_confirmed: false });
        }
        await saveWeighin(id, row, 'pending');
        setRow(id, unconfirm
          ? { status: 'pending', confirmed: false, lastServerConfirmed: false }
          : { status: 'pending' });
        await afterWrite({ source: 'CheckinTab:reset', refresh: async () => onRegistrationUpdated() });
      } catch (err) {
        setRow(id, { error: inlineError(err, ci.resetFailed) });
      }
    });
  }

  // ── Filter & sort ────────────────────────────────────────────────────────────
  // Pending first → absent → ready; within each group sort by last name

  const STATUS_ORDER: Record<WeighinStatus, number> = { pending: 0, absent: 1, ready: 2 };

  const filtered = registrations
    // The name in either order (#479), or the team (#661, F30).
    .filter(reg => nameMatches(search, reg.first_name, reg.last_name)
      || textMatches(search, reg.team_name))
    .sort((a, b) => {
      const sa = rows[a.id]?.status ?? 'pending';
      const sb = rows[b.id]?.status ?? 'pending';
      return STATUS_ORDER[sa] - STATUS_ORDER[sb] || compareNames(a, b);
    });

  // Summary counts
  const total   = registrations.length;
  const ready   = registrations.filter(r => rows[r.id]?.status === 'ready').length;
  const absent  = registrations.filter(r => rows[r.id]?.status === 'absent').length;
  const pending = total - ready - absent;
  /** The End Check-in button shows (the rule of #490 and #547, below). */
  const showEnd = total > 0 && (pending > 0 || !lockedBy);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* #490 — the lock notice. On 2026-09-26 a manager clicked Ready 12
          times under "Lock registration", and each click failed. */}
      {lockedBy && (
        <div data-testid="checkin-lock-notice"
          className="card p-3 bg-amber-50 border border-amber-200 text-sm text-amber-800 flex flex-wrap items-center gap-3">
          <span className="flex-1 min-w-0">
            {lockedBy === 'bouts' ? ci.boutsLockedNotice : ci.registrationLockedNotice}
          </span>
          {lockedBy === 'registration' && onUnlockRegistration && (
            <button onClick={onUnlockRegistration} disabled={unlockBusy} data-testid="checkin-unlock-registration"
              className="btn-outline btn-sm">
              {t.tournament.unlockRegistration}
            </button>
          )}
        </div>
      )}

      {/* #557 — End Check-in sends the summary of the managers as a push:
          this device has no push subscription. Above the End button, while
          the button shows. */}
      {showEnd && <PushOffHint />}

      {/* Summary + search */}
      <div className="flex flex-wrap gap-3 items-center">
        <span className="text-sm font-medium text-gray-700">{boxerCount(total)}</span>
        <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-semibold">
          {pending} {ci.pending}
        </span>
        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold">
          {readyCount(ready)}
        </span>
        {absent > 0 && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-semibold">
            {absent} {ci.absent}
          </span>
        )}
        {showEnd && (
          // #490 — off under a lock too (operator, 2026-09-26): after its
          // dialog, one click marks every pending athlete absent.
          // #547 — also shown while registration and the bouts are not
          // locked, when no athlete is pending. "Disable coach corrections"
          // does not hide it. At full turnout the End still locks
          // registration and sends the check-in summary. After "Unlock
          // registration" the End closes a correction again. The End sets
          // the lock, so it hides. Not shown with no registration: the End
          // stores the ended day, and the page cannot remove it.
          <button onClick={handleEndCheckin} disabled={ending.busy || !!lockedBy} data-testid="checkin-end"
            className="btn-outline-red btn-sm disabled:opacity-50 ml-auto">
            {ending.busy ? '…' : `${ci.endCheckinBtn} (${readyCount(ready)}, ${pending} ${ci.pending})`}
          </button>
        )}
        <input
          type="search"
          placeholder={ci.searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="ml-auto border border-gray-300 rounded px-3 py-1 text-sm
                     focus:outline-hidden focus:ring-1 focus:ring-blue-500 w-full sm:w-56"
        />
      </div>

      {/* Table. #552 — on a phone (360-390 px) the weight field and the
          Ready / Reset button of every row show with no sideways scroll:
          the name wraps, the cells are narrow, and the Status column is
          hidden. The Actions cell shows the status there. */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 sm:whitespace-nowrap">{ci.athlete}</th>
              <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 sm:whitespace-nowrap">{ci.actualWeight}</th>
              <th className="hidden sm:table-cell px-3 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">{ci.status}</th>
              <th className="px-2 sm:px-3 py-2 text-right text-xs font-medium text-gray-500 sm:whitespace-nowrap">{ci.actions}</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  {search ? ci.noMatch : ci.noRegistrations}
                </td>
              </tr>
            )}

            {filtered.map(reg => {
              const row = rows[reg.id];
              if (!row) return null;

              const saving   = rowBusy.isBusy(reg.id);
              const isReady  = row.status === 'ready';
              const isAbsent = row.status === 'absent';

              // Highlight done rows
              const rowBg = isReady
                ? 'bg-green-50'
                : isAbsent
                ? 'bg-red-50 opacity-70'
                : '';

              return (
                <tr key={reg.id} data-testid="checkin-row" data-registration-id={reg.id} className={`${rowBg} transition-colors`}>

                  {/* Name + team */}
                  <td className="px-2 sm:px-4 py-2 sm:whitespace-nowrap">
                    <div className="font-medium text-gray-900 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <span>{personName(reg.first_name, reg.last_name)}</span>
                      <GenderBadge gender={reg.gender ?? ''} short size="xs" />
                      {reg.age && <span className="text-[10px] text-gray-400 whitespace-nowrap">{reg.age} {ci.yrs}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 mt-0.5">
                      {reg.team_name && <span className={`badge badge-gray ${BADGE_XXS} px-1.5`} title={t.common.team}>{reg.team_name}</span>}
                      {reg.age_group && <span className={`badge badge-amber ${BADGE_XXS}`}>{reg.age_group}</span>}
                      {reg.weight_class && <span className={`badge badge-gray ${BADGE_XXS}`}>{reg.weight_class}</span>}
                      {reg.in_bracket && (
                        <span className={`badge badge-purple ${BADGE_XXS}`} title={ci.inBracketTitle} data-testid="checkin-in-bracket">
                          {ci.inBracket}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Actual weight input */}
                  <td className="px-2 sm:px-3 py-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      min={20}
                      max={200}
                      step={0.1}
                      placeholder={reg.current_weight_kg ? String(reg.current_weight_kg) : 'kg'}
                      value={row.weight_kg}
                      disabled={saving || isReady}
                      onChange={e => setRow(reg.id, { weight_kg: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm
                                 focus:outline-hidden focus:ring-1 focus:ring-blue-500
                                 disabled:bg-gray-100 disabled:text-gray-400 w-16 sm:w-20"
                    />
                  </td>

                  {/* Status pill. Hidden on a phone: the Actions cell shows
                      the status there. */}
                  <td className="hidden sm:table-cell px-3 py-2 text-center">
                    <StatusPill status={row.status} labels={ci} />
                  </td>

                  {/* Actions. The error text wraps, so it does not make the
                      column wide on a phone. */}
                  <td className="px-2 sm:px-3 py-2 text-right">
                    {row.error && (
                      <Notice tone="error" plain size="xs" className="mb-1 max-w-xs ml-auto text-right">
                        {row.error}
                      </Notice>
                    )}

                    {/* Not yet marked */}
                    {!isReady && !isAbsent && (
                      <div className="flex gap-1.5 justify-end flex-wrap">
                        <button
                          onClick={() => markReady(reg.id)}
                          disabled={saving || !row.weight_kg || !!lockedBy}
                          data-testid="checkin-ready"
                          className="btn-sm bg-green-600 text-white hover:bg-green-700
                                     disabled:opacity-50 whitespace-nowrap"
                          title={!row.weight_kg ? ci.enterWeight : ci.markReady}
                        >
                          {saving ? '…' : <><CheckIcon className="w-3.5 h-3.5" />{ci.readyBtn}</>}
                        </button>
                      </div>
                    )}

                    {/* Already marked → allow undo. On a phone the Status
                        column is hidden: the status shows above the button
                        as text, not only as a row colour, and Reset is an
                        icon (the label stays for a screen reader and as the
                        title). Side by side, the LT "neatvyko" and "Nustatyti
                        iš naujo" made the column too wide for 360 px. */}
                    {(isReady || isAbsent) && (
                      <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:justify-end sm:gap-1.5">
                        <span className="sm:hidden"><StatusPill status={row.status} labels={ci} /></span>
                        <button
                          onClick={() => resetPending(reg.id)}
                          disabled={saving || !!lockedBy}
                          data-testid="checkin-reset"
                          title={ci.resetBtn}
                          className="btn-sm border border-gray-300 text-gray-600
                                     hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1 whitespace-nowrap"
                        >
                          {saving ? '…' : <><UndoIcon className="w-3 h-3" /> <span className="max-sm:sr-only">{ci.resetBtn}</span></>}
                        </button>
                      </div>
                    )}
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">{ci.hint}</p>
    </div>
  );
}
